import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SubscriptionStats {
  free: number;
  standard: number;
  pro: number;
  total: number;
}

export interface ChartDataPoint {
  date: string;
  count: number;
}

export function useSubscriptionStats() {
  const [loading, setLoading] = useState(false);

  const getSubscriptionDemographics = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all profiles to count subscription tiers directly
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_status');

      if (profilesError) throw profilesError;

      const stats: SubscriptionStats = {
        free: 0,
        standard: 0,
        pro: 0,
        total: profiles?.length || 0,
      };

      profiles?.forEach((profile) => {
        const tier = profile.subscription_tier;
        const status = profile.subscription_status;
        
        // Count as active if status is active, trialing, or gifted
        const isActive = ['active', 'trialing', 'gifted'].includes(status || '');
        
        if (isActive) {
           if (tier === 'pro') {
             stats.pro++;
           } else if (tier === 'standard' || tier === 'basic') {
             stats.standard++;
           } else {
             stats.free++;
           }
        } else {
           // Inactive or other statuses count as free/no active paid plan
           stats.free++;
        }
      });
      
      // Safety check: ensure free + standard + pro = total
      // The logic above forces every user into one bucket, but let's recalculate free to be sure
      // actually the logic `else { stats.free++ }` covers all non-paid cases.
      
      return stats;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('獲取訂閱數據失敗: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSubscriptionTrends = useCallback(async (days: number = 30) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('created_at')
        .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const trends: Record<string, number> = {};
      data?.forEach((sub: { created_at?: string | null }) => {
        const date = new Date(sub.created_at || 0).toLocaleDateString();
        trends[date] = (trends[date] || 0) + 1;
      });

      const chartData: ChartDataPoint[] = Object.entries(trends).map(([date, count]) => ({
        date,
        count,
      }));

      return chartData;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error('獲取趨勢數據失敗: ' + message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    getSubscriptionDemographics,
    getSubscriptionTrends,
  };
}
